/*
  IA PDP Sticky Bar — <ia-pdp-sticky> (blocks/_ia-pdp-sticky-bar.liquid).

  - Reveal: .is-visible (scrolled past the main buy area) drives the anchors
    bar and the desktop ATC pill; .is-atc-visible drives the ATC bar, tracking
    the add-to-bag button out of view either way on mobile.
  - Anchors: builds the section jump links by scanning the .shopify-section
    siblings AFTER the main product section (section groups — header,
    footer, aside drawers — are excluded by their shopify-section-group-*
    class, so only template content sections qualify). Label = the
    section's [data-section-name] value (the "Section name" setting each
    PDP content section renders on its root; an empty value excludes the
    section), with the first h1/h2 text as fallback for sections without
    the attribute; sections with neither are skipped. An optional first link
    (the block's "First link label", passed as data-top-anchor) scrolls back
    to the top of the page instead of to a section. The active link follows
    the scroll position; clicks ask the header to hide (`ia-header:hide`) and
    scroll to an offset that only has to clear the mobile anchors bar — the
    scroll-margin on the sections stays for plain #hash landings.
  - Variant dropdown (mobile ATC): drives the main <ia-product-options>
    picker so the whole PDP re-renders in sync (the ATC container carries
    data-re-render and is swapped by <ia-pdp>). Without a picker on the
    page it swaps the sticky form's variant id + price in place.

  Listeners are delegated on the root, so the data-re-render swap of the
  ATC container needs no rebinding.
*/
import { EVENTS, publish } from 'ia-util-pubsub'

// Mobile pins the anchors bar to the top; desktop is a bottom-left pill (CSS).
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)')
// No cross-browser "smooth scroll finished" event yet — Safari lacks scrollend.
const SCROLL_IDLE_MS = 150
// How much of the main ATC must show before the sticky bar steps aside (px).
const ATC_OFFSET = 16

if (!customElements.get('ia-pdp-sticky')) {
  class IaPdpSticky extends HTMLElement {
    connectedCallback() {
      // The quick-buy drawer re-renders the PDP section — no fixed bars there.
      if (this.closest('ia-quick-buy')) return

      this.abortController = new AbortController()
      const { signal } = this.abortController

      this.pdp = this.closest('ia-pdp')
      this.anchorsNav = this.querySelector('[data-sticky-anchors]')
      this.atcBar = this.querySelector('.ia-pdp-sticky__atc')
      this.buildAnchors()

      this.addEventListener('change', this.handleSelectChange.bind(this), { signal })
      this.addEventListener('click', this.handleAnchorClick.bind(this), { signal })
      window.addEventListener('scroll', this.requestUpdate.bind(this), { signal, passive: true })
      window.addEventListener('resize', this.requestUpdate.bind(this), { signal })

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:load', this.buildAnchors.bind(this), { signal })
        document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this), { signal })
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect.bind(this), { signal })
      }

      this.update()
    }

    disconnectedCallback() {
      this.abortController?.abort()
      this.scrollEndController?.abort()
      clearTimeout(this.scrollIdleTimer)
      document.documentElement.style.setProperty('--ia-anchors-height', '0px')
      document.documentElement.style.removeProperty('--ia-pdp-sticky-height')
    }

    /* ---- reveal ---- */

    requestUpdate() {
      if (this.updateQueued) return
      this.updateQueued = true
      requestAnimationFrame(() => {
        this.update()
        this.updateQueued = false
      })
    }

    update() {
      this.updateVisibility()
      this.syncAnchorsHeight()
      this.syncAtcHeight()
      this.updateActiveAnchor()
    }

    // ATC bar reach (height + bottom offset, 0 while hidden) on the root so the chat launcher clears it.
    syncAtcHeight() {
      const bar = this.atcBar
      const shown = bar && !bar.hidden && this.classList.contains('is-atc-visible')
      const reach = shown ? bar.offsetHeight + (parseFloat(getComputedStyle(bar).bottom) || 0) : 0
      document.documentElement.style.setProperty('--ia-pdp-sticky-height', `${Math.round(reach)}px`)
    }

    /* Publish the bar's height on <html> so anything scrolling an element into
       view can clear it. Desktop renders it as a bottom-left pill, so it adds
       nothing to the top offset there. */
    syncAnchorsHeight() {
      const nav = this.anchorsNav
      const stacked = nav && !nav.hidden && !DESKTOP_MQ.matches && this.classList.contains('is-visible')
      document.documentElement.style.setProperty('--ia-anchors-height', stacked ? `${nav.offsetHeight}px` : '0px')
    }

    updateVisibility() {
      if (this.editorSelected) return
      const trigger = this.pdp?.querySelector('.ia-pdp-buy, .ia-bundle__atc')
      const passed = trigger
        ? trigger.getBoundingClientRect().bottom < 0
        : window.scrollY > window.innerHeight * 0.5
      this.classList.toggle('is-visible', passed)
      this.classList.toggle('is-atc-visible', DESKTOP_MQ.matches ? passed : this.isMainAtcHidden(trigger))
    }

    // Mobile: hidden = the main button is out of view above OR below the fold.
    isMainAtcHidden(trigger) {
      const atc = trigger?.querySelector('button[name="add"]') || trigger
      if (!atc) return window.scrollY > window.innerHeight * 0.5
      const rect = atc.getBoundingClientRect()
      if (!rect.width && !rect.height) return true
      return rect.bottom < ATC_OFFSET || rect.top > window.innerHeight - ATC_OFFSET
    }

    /* ---- section anchors ---- */

    buildAnchors() {
      const list = this.querySelector('[data-anchor-list]')
      const template = this.querySelector('[data-anchor-template]')
      this.anchorTargets = []
      if (!list || !template) return

      list.textContent = ''

      /* Optional first link ("First link label" setting) — a back-to-top action
         rather than a section, so it stays out of anchorTargets and never takes
         the active state. */
      const topLabel = this.anchorsNav?.dataset.topAnchor?.trim()
      if (topLabel) {
        const topItem = template.content.firstElementChild.cloneNode(true)
        const topLink = topItem.querySelector('a')
        topLink.href = '#'
        topLink.textContent = topLabel
        topLink.setAttribute('data-anchor-top', '')
        list.appendChild(topItem)
      }

      this.getContentSections().forEach((sectionEl) => {
        const label = this.getAnchorLabel(sectionEl)
        if (!label) return
        const item = template.content.firstElementChild.cloneNode(true)
        const link = item.querySelector('a')
        link.href = `#${sectionEl.id}`
        link.textContent = label
        sectionEl.style.scrollMarginTop = 'calc(var(--ia-header-height, 0px) + 16px)'
        list.appendChild(item)
        this.anchorTargets.push({ sectionEl, link })
      })

      // Count rendered links, not just section targets — a lone top link still shows.
      this.anchorsNav?.toggleAttribute('hidden', list.children.length === 0)
    }

    getContentSections() {
      const sections = []
      let el = this.closest('.shopify-section')?.nextElementSibling
      while (el) {
        if (el.classList.contains('shopify-section') && !el.className.includes('shopify-section-group-')) {
          sections.push(el)
        }
        el = el.nextElementSibling
      }
      return sections
    }

    getAnchorLabel(sectionEl) {
      const named = sectionEl.querySelector('[data-section-name]')
      // attribute present but empty = the merchant excluded the section
      if (named) return named.dataset.sectionName.trim()
      return sectionEl.querySelector('h1, h2')?.textContent.trim() || ''
    }

    handleAnchorClick(event) {
      const link = event.target.closest('.ia-pdp-sticky__anchor')
      if (!link) return
      if (link.hasAttribute('data-anchor-top')) {
        event.preventDefault()
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      const target = document.getElementById(link.hash.slice(1))
      if (!target) return
      event.preventDefault()
      this.scrollToSection(target)
    }

    /* The header's own visibility depends on scroll direction, so it can't be
       part of the offset — a jump down lands under a hidden header, a jump up
       under a visible one. Instead: ask it to hide and hold, then offset only by
       what stays on screen (the mobile anchors bar) — the section's own padding
       is the breathing room. */
    scrollToSection(target) {
      publish(EVENTS.headerHide, { detail: { locked: true } })
      const barHeight =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ia-anchors-height')) || 0
      const top = target.getBoundingClientRect().top + window.scrollY - barHeight
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
      this.releaseHeaderAfterScroll()
    }

    /* Release on the native scrollend where it exists, otherwise once scrolling
       has gone quiet — covers "the page was already there and never moved" too. */
    releaseHeaderAfterScroll() {
      this.scrollEndController?.abort()
      this.scrollEndController = new AbortController()
      const { signal } = this.scrollEndController

      const release = () => {
        clearTimeout(this.scrollIdleTimer)
        this.scrollEndController?.abort()
        this.scrollEndController = null
        publish(EVENTS.headerHide, { detail: { locked: false } })
      }
      const restartIdle = () => {
        clearTimeout(this.scrollIdleTimer)
        this.scrollIdleTimer = setTimeout(release, SCROLL_IDLE_MS)
      }

      window.addEventListener('scrollend', release, { signal })
      window.addEventListener('scroll', restartIdle, { signal, passive: true })
      restartIdle()
    }

    updateActiveAnchor() {
      if (!this.anchorTargets?.length) return
      const probe = window.innerHeight * 0.4
      let active = null
      this.anchorTargets.forEach((entry) => {
        if (entry.sectionEl.getBoundingClientRect().top <= probe) active = entry
      })
      this.anchorTargets.forEach((entry) => {
        entry.link.classList.toggle('is-active', entry === active)
        if (entry === active) {
          entry.link.setAttribute('aria-current', 'true')
        } else {
          entry.link.removeAttribute('aria-current')
        }
      })
    }

    /* ---- variant dropdown ---- */

    handleSelectChange(event) {
      const select = event.target.closest('[data-sticky-variant]')
      if (!select) return
      const picker = this.pdp?.querySelector('ia-product-options')
      if (picker && this.syncPicker(picker, select.value)) return
      this.applyLocalVariant(select)
    }

    /* Mirror the chosen variant's option values onto the main picker and let
       it own the fetch + variant:change publish (which re-renders this ATC). */
    syncPicker(picker, variantId) {
      const dataScript = picker.querySelector('script[type="application/json"]')
      if (!dataScript) return false
      const variant = JSON.parse(dataScript.textContent).find((entry) => String(entry.id) === String(variantId))
      if (!variant) return false

      picker.querySelectorAll('select, .js-option-radio').forEach((group, index) => {
        const value = variant.options[index]
        if (group.tagName === 'SELECT') {
          group.value = value
        } else {
          const radio = [...group.querySelectorAll('input')].find((input) => input.value === value)
          if (radio) radio.checked = true
        }
      })
      picker.dispatchEvent(new Event('change'))
      return true
    }

    /* No picker on the page — swap the sticky form's id + price in place
       (same in-place pattern as the product-card size select). */
    applyLocalVariant(select) {
      const option = select.selectedOptions[0]
      if (!option) return

      const idInput = this.querySelector('form input[name="id"]')
      if (idInput) idInput.value = select.value

      const salePrice = this.querySelector('[data-sale-price]')
      if (salePrice && option.dataset.variantPrice) salePrice.textContent = option.dataset.variantPrice

      const comparePrice = this.querySelector('[data-sale-price-compare]')
      if (comparePrice) {
        comparePrice.textContent = option.dataset.variantComparePrice || ''
        comparePrice.style.display = option.dataset.variantComparePrice ? '' : 'none'
      }
    }

    /* ---- theme editor ---- */

    handleBlockSelect(event) {
      if (event.target !== this && !this.contains(event.target)) return
      this.editorSelected = true
      this.classList.add('is-visible', 'is-atc-visible')
      this.syncAtcHeight()
    }

    handleBlockDeselect(event) {
      if (event.target !== this && !this.contains(event.target)) return
      this.editorSelected = false
      this.update()
    }
  }

  customElements.define('ia-pdp-sticky', IaPdpSticky)
}
